import React, {useLayoutEffect, useMemo, useRef, useState} from 'react';
import {Box, styled} from '@mui/material';
import {Body, designSystemToken, Label} from '@lightricks/react-design-system';

const DEFAULT_SETTINGS = {
  backgroundColor: undefined,
  borderRadius: undefined,
  optionColor: undefined,
  selectedOptionColor: undefined,
  optionBorderRadius: undefined,
};

export interface ToggleButtonOption {
  label: string;
  value: string;
  icon?: React.ReactNode;
  minWidth?: string;

  flex?: number;
}

export type ToggleButtonLayout = 'equal' | 'content' | 'custom';

interface Settings {
  backgroundColor?: string;
  borderRadius?: string;
  optionColor?: string;
  selectedOptionColor?: string;
  optionBorderRadius?: string;
}

interface ToggleButtonProps {
  testID?: string;
  options: ToggleButtonOption[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  showBorder?: boolean;
  borderRadius?: string;
  layout?: ToggleButtonLayout;
  gap?: string;
  padding?: string;
  disableSlideAnimation?: boolean;
  settings?: Settings;
}

interface ContainerProps {
  disabled: boolean;
  showBorder: boolean;
  borderRadius: string;
  gap: string;
  padding: string;
  settings: Settings;
}

const Container = styled(Box, {
  shouldForwardProp: (prop) =>
    ![
      'disabled',
      'showBorder',
      'borderRadius',
      'gap',
      'padding',
      'settings',
    ].includes(prop as string),
})<ContainerProps>(
  ({disabled, showBorder, borderRadius, gap, padding, settings}) => ({
    position: 'relative',
    display: 'flex',
    background:
      settings.backgroundColor ||
      (showBorder ? designSystemToken('semantic.bg.secondary') : 'transparent'),
    borderRadius: (() => {
      if (showBorder) {
        return borderRadius;
      }

      return settings.borderRadius || '16px';
    })(),
    border: showBorder
      ? `1px solid ${designSystemToken('semantic.bg.neutral-secondary')}`
      : 'none',
    width: '100%',
    minHeight: '48px',
    overflow: 'hidden',
    opacity: disabled ? 0.5 : 1,
    gap,
    padding,
    alignItems: 'center',
    boxSizing: 'border-box',
  })
);

function getFlexStyles(layout: ToggleButtonLayout, option: ToggleButtonOption) {
  switch (layout) {
    case 'equal':
      return {flex: 1};
    case 'content':
      return {flex: 'none', width: 'auto'};
    case 'custom':
      return {
        flex: option.flex || 'none',
        minWidth: option.minWidth || 'auto',
      };
    default:
      return {flex: 1};
  }
}

interface OptionButtonProps {
  selected?: boolean;
  disabled?: boolean;
  layout: ToggleButtonLayout;
  option: ToggleButtonOption;
  showBorder: boolean;
  settings: Settings;
}

const OptionButton = styled(Box, {
  shouldForwardProp: (prop) =>
    ![
      'selected',
      'disabled',
      'layout',
      'option',
      'showBorder',
      'settings',
    ].includes(prop as string),
})<OptionButtonProps>(({
  selected,
  disabled,
  layout,
  option,
  showBorder,
  settings,
}) => {
  const borderRadius =
    settings.optionBorderRadius || (showBorder ? '0' : '12px');

  let backgroundColor: string;
  if (showBorder && selected) {
    backgroundColor = 'transparent';
  } else if (selected && settings.selectedOptionColor) {
    backgroundColor = settings.selectedOptionColor;
  } else if (!showBorder && selected) {
    backgroundColor = designSystemToken('semantic.bg.tertiary');
  } else {
    backgroundColor = settings.optionColor || 'transparent';
  }

  return {
    ...getFlexStyles(layout, option),
    zIndex: 2,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: showBorder ? '8px 0' : '6px 12px',
    gap: option.icon ? '8px' : '0',
    fontWeight: selected ? 500 : 400,
    fontSize: '14px',
    lineHeight: '18px',
    color: designSystemToken('semantic.fg.primary'),
    transition: 'font-weight 0.2s',
    userSelect: 'none',
    pointerEvents: disabled ? 'none' : 'auto',
    borderRadius,
    backgroundColor,
    minHeight: !showBorder ? '32px' : undefined,
  };
});

const slideNotForwardProps = ['enableTransition', 'slideBorderRadius'];

const Slide = styled(Box, {
  shouldForwardProp: (prop) => !slideNotForwardProps.includes(prop as string),
})<{
  left: number;
  width: number;
  enableTransition: boolean;
  slideBorderRadius: string;
}>(({left, width, enableTransition, slideBorderRadius}) => ({
  position: 'absolute',
  left,
  width,
  height: 'calc(100% - 10px)',
  boxSizing: 'border-box',
  background: designSystemToken('semantic.bg.secondary'),
  borderRadius: slideBorderRadius,
  boxShadow: `0 0 0 1px ${designSystemToken('semantic.fg.primary')}`,
  transition: enableTransition
    ? 'left 0.3s cubic-bezier(0.4,0,0.2,1), width 0.3s cubic-bezier(0.4,0,0.2,1)'
    : 'none',
  zIndex: 1,
}));

function OptionContent({
  option,
  selected,
  showBorder,
}: {
  option: ToggleButtonOption;
  selected: boolean;
  showBorder: boolean;
}) {
  let textElement;
  if (showBorder) {
    textElement = selected ? (
      <Label size="md" color={designSystemToken('semantic.fg.primary')}>
        {option.label}
      </Label>
    ) : (
      <Body size="md" color={designSystemToken('semantic.fg.primary')}>
        {option.label}
      </Body>
    );
  } else {
    textElement = <Body size="md">{option.label}</Body>;
  }

  if (option.icon) {
    return (
      <>
        {option.icon}
        {textElement}
      </>
    );
  }

  return textElement;
}

function ToggleButton({
  testID = 'toggle-button',
  options,
  value,
  onChange,
  disabled = false,
  showBorder = true,
  borderRadius = '16px',
  layout = 'equal',
  gap = '0',
  padding = '0',
  disableSlideAnimation = false,
  settings = {},
}: ToggleButtonProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [slideStyle, setSlideStyle] = useState({left: 0, width: 0});
  const [hasMeasured, setHasMeasured] = useState(false);
  const [enableTransition, setEnableTransition] = useState(false);
  const mergedSettings = useMemo(
    () => ({...DEFAULT_SETTINGS, ...settings}),
    [settings]
  );

  useLayoutEffect(() => {
    if (disableSlideAnimation || !showBorder || !containerRef.current) {
      return;
    }
    const idx = options.findIndex((opt) => opt.value === value);
    if (idx === -1) {
      return;
    }
    const {children} = containerRef.current;
    // Slide is not rendered on first pass, so children[idx] is the option
    // On subsequent passes, Slide is first, so children[idx + 1] is the option
    const optionEl = hasMeasured
      ? (children[idx + 1] as HTMLElement)
      : (children[idx] as HTMLElement);
    if (optionEl) {
      setSlideStyle({
        left: optionEl.offsetLeft,
        width: optionEl.offsetWidth,
      });
      if (!hasMeasured) {
        setHasMeasured(true);
        // Enable transition after the first measurement/render
        requestAnimationFrame(() => setEnableTransition(true));
      }
    }
  }, [disableSlideAnimation, showBorder, value, options, hasMeasured]);

  return (
    <Container
      ref={containerRef}
      data-testid={testID}
      disabled={disabled}
      showBorder={showBorder}
      borderRadius={borderRadius}
      gap={gap}
      padding={padding}
      settings={mergedSettings}
    >
      {!disableSlideAnimation && showBorder && hasMeasured ? (
        <Slide
          left={slideStyle.left}
          width={slideStyle.width}
          enableTransition={enableTransition}
          slideBorderRadius={mergedSettings.optionBorderRadius || '12px'}
        />
      ) : null}
      {options.map((option) => {
        const isSelected = option.value === value;
        return (
          <OptionButton
            key={option.value}
            selected={isSelected}
            layout={layout}
            option={option}
            showBorder={showBorder}
            onClick={() => {
              if (!disabled) {
                onChange(option.value);
              }
            }}
            disabled={disabled}
            aria-pressed={isSelected}
            tabIndex={disabled ? -1 : 0}
            role="button"
            settings={mergedSettings}
          >
            <OptionContent
              option={option}
              selected={isSelected}
              showBorder={showBorder}
            />
          </OptionButton>
        );
      })}
    </Container>
  );
}

export default ToggleButton;
